import type { WorkOrderRow } from './types';

/**
 * Selectable statuses for the Project Plan's Band 2 status combobox
 * (ENG-720). Also the canonical chip order: `MultiSelectCombobox` renders
 * chips in the order values appear in the controlled `value` array, so the
 * filter bar sorts the selection into this order before writing to the URL.
 *
 * `archived` is deliberately absent — archived work orders are governed by the
 * separate "Show Archived" toggle, which decides whether they are fetched and
 * merged into the grid at all. Once merged they are ordinary rows: an empty
 * selection shows them, and picking any status hides them (they can't match).
 */
export const PROJECT_WORK_ORDER_STATUS_FILTERS = [
  'draft',
  'redrafting',
  'scheduled',
  'in_progress',
  'complete',
] as const;

export type ProjectWorkOrderStatusFilter = (typeof PROJECT_WORK_ORDER_STATUS_FILTERS)[number];

/** Only the status is read, so both grid rows and raw detail rows fit. */
type StatusableRow = Pick<WorkOrderRow, 'work_order_current_status'>;

/**
 * Multi-select status match for one project's work orders.
 *
 * An empty selection means "everything currently loaded" — **including
 * drafts**. This is a deliberate divergence from the Production Planner's
 * `status-filter.ts`, where an empty selection hides drafts: partner-wide, a
 * draft is noise; inside a single project it is the primary content, and a
 * draft project consists of nothing else. Hiding drafts by default would open
 * the page on an empty grid.
 *
 * With one or more statuses selected a row matches if its status is one of
 * them — exact equality, no folding. The planner absorbs `redrafting` into
 * `scheduled` because it surfaces redrafting through a separate call-out pill;
 * here `redrafting` is its own combobox option and the project-level state
 * already reads from the Band 1 chip, so no folding is needed.
 */
export function matchesProjectStatusFilter(
  row: StatusableRow,
  statuses: readonly ProjectWorkOrderStatusFilter[],
): boolean {
  if (statuses.length === 0) return true;
  return statuses.some((status) => status === row.work_order_current_status);
}

/** Option counts for the Band 2 status combobox; see {@link countProjectWorkOrderStatuses}. */
export interface ProjectWorkOrderStatusCounts {
  draft: number;
  redrafting: number;
  scheduled: number;
  inProcess: number;
  complete: number;
  /** Every non-archived row — what an empty selection shows, archived aside. */
  everything: number;
}

/**
 * Option counts in a single pass over `rows`, rather than filtering per
 * option. Archived rows are skipped: they're never a combobox option, so
 * counting them would make the totals disagree with what selecting an option
 * actually yields.
 */
export function countProjectWorkOrderStatuses(
  rows: readonly StatusableRow[],
): ProjectWorkOrderStatusCounts {
  const counts: ProjectWorkOrderStatusCounts = {
    draft: 0,
    redrafting: 0,
    scheduled: 0,
    inProcess: 0,
    complete: 0,
    everything: 0,
  };

  for (const row of rows) {
    switch (row.work_order_current_status) {
      case 'archived':
        continue;
      case 'draft':
        counts.draft += 1;
        break;
      case 'redrafting':
        counts.redrafting += 1;
        break;
      case 'scheduled':
        counts.scheduled += 1;
        break;
      case 'in_progress':
        counts.inProcess += 1;
        break;
      case 'complete':
        counts.complete += 1;
        break;
    }
    counts.everything += 1;
  }

  return counts;
}
